'use client';

import { FormEvent, useEffect, useState } from 'react';
import { useLocale } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Check, Loader2, Wrench, MapPin, Euro, ShieldCheck, Users } from 'lucide-react';

type Locale = 'nl' | 'de' | 'en' | 'fr';

const copy: Record<Locale, {
  title: string; subtitle: string;
  intro: string; intro2: string;
  benefits: string[];
  benefitTitles: string[];
  form: { title: string; name: string; email: string; phone: string; website: string; address: string; city: string; postalCode: string; country: string; radius: string; capabilities: string; notes: string; submit: string; submitting: string };
  success: string; error: string;
}> = {
  nl: {
    title: 'Word montagepartner',
    subtitle: 'Sluit je aan als signbedrijf en ontvang montage-opdrachten van Printen.eu klanten bij jou in de buurt.',
    intro: 'Printen.eu groeit snel en onze klanten hebben steeds vaker montage nodig. Denk aan lichtbakken ophangen, raamstickers aanbrengen, banners bevestigen en gevelreclame monteren.',
    intro2: 'Als montagepartner kom je in ons netwerk van signbedrijven. Klanten in jouw regio kunnen via onze website montage aanvragen. Onze AI matcht de klus aan het best passende bedrijf op basis van afstand en specialisatie.',
    benefits: ['Klanten in je eigen regio', 'Flexibel — je kiest zelf je straal en specialisaties', 'Geen acquisitiekosten', 'Automatische matching via AI', 'Uurtarief vanaf €85'],
    benefitTitles: ['Lokale opdrachten', 'Flexibel', 'Geen acquisitie', 'AI matching', 'Goed tarief'],
    form: { title: 'Aanmelden', name: 'Bedrijfsnaam', email: 'E-mail', phone: 'Telefoon', website: 'Website', address: 'Adres', city: 'Plaats', postalCode: 'Postcode', country: 'Land', radius: 'Max. reisafstand (km)', capabilities: 'Welke werkzaamheden voer je uit? (vink aan wat van toepassing is)', notes: 'Extra toelichting (optioneel)', submit: 'Aanmelden', submitting: 'Aanmelden...' },
    success: 'Je aanmelding is ontvangen! We nemen binnen 2 werkdagen contact met je op.',
    error: 'Aanmelding mislukt. Probeer het opnieuw of mail naar info@printen.eu.',
  },
  de: {
    title: 'Montagepartner werden',
    subtitle: 'Schließen Sie sich als Schilderbetrieb an und erhalten Sie Montageauftrage von Printen.eu-Kunden in Ihrer Nahe.',
    intro: 'Printen.eu wachst schnell und unsere Kunden benotigen zunehmend Montage. Zum Beispiel Leuchtkasten aufhangen, Fensterfolien anbringen, Banner befestigen und Fassadenwerbung montieren.',
    intro2: 'Als Montagepartner werden Sie Teil unseres Netzwerks. Kunden in Ihrer Region konnen uber unsere Website Montage anfordern. Unsere KI ordnet den Auftrag dem am besten geeigneten Betrieb zu.',
    benefits: ['Kunden in Ihrer Region', 'Flexibel', 'Keine Akquisekosten', 'Automatische KI-Vermittlung', 'Stundensatz ab €85'],
    benefitTitles: ['Lokale Auftrage', 'Flexibel', 'Keine Akquise', 'KI-Vermittlung', 'Guter Tarif'],
    form: { title: 'Anmeldung', name: 'Firmenname', email: 'E-Mail', phone: 'Telefon', website: 'Website', address: 'Adresse', city: 'Stadt', postalCode: 'PLZ', country: 'Land', radius: 'Max. Entfernung (km)', capabilities: 'Welche Arbeiten fuhren Sie durch?', notes: 'Zusatzliche Anmerkungen (optional)', submit: 'Anmelden', submitting: 'Wird gesendet...' },
    success: 'Ihre Anmeldung ist eingegangen! Wir melden uns innerhalb von 2 Werktagen.',
    error: 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut oder mailen Sie an info@printen.eu.',
  },
  en: {
    title: 'Become an installation partner',
    subtitle: 'Join as a sign company and receive installation orders from Printen.eu customers near you.',
    intro: 'Printen.eu is growing fast and our customers increasingly need installation services — hanging lightboxes, applying window graphics, mounting banners, and installing facade signage.',
    intro2: 'As an installation partner, you join our network. Customers in your region can request installation through our website. Our AI matches the job to the best-suited company based on distance and expertise.',
    benefits: ['Customers in your region', 'Flexible — choose your radius and specialties', 'No acquisition costs', 'Automatic AI matching', 'Hourly rate from €85'],
    benefitTitles: ['Local orders', 'Flexible', 'No acquisition', 'AI matching', 'Great rate'],
    form: { title: 'Sign up', name: 'Company name', email: 'Email', phone: 'Phone', website: 'Website', address: 'Address', city: 'City', postalCode: 'Postal code', country: 'Country', radius: 'Max. travel distance (km)', capabilities: 'Which services do you provide?', notes: 'Additional notes (optional)', submit: 'Apply', submitting: 'Submitting...' },
    success: 'Your application has been received! We will contact you within 2 business days.',
    error: 'Registration failed. Please try again or email info@printen.eu.',
  },
  fr: {
    title: 'Devenir partenaire installation',
    subtitle: 'Rejoignez-nous en tant qu’entreprise et recevez des commandes d’installation de clients Printen.eu pres de chez vous.',
    intro: 'Printen.eu se developpe rapidement et nos clients ont de plus en plus besoin d’installation — accrocher des enseignes lumineuses, poser des vinyles, installer des bâches et monter de la signalétique de façade.',
    intro2: 'En tant que partenaire, vous rejoignez notre réseau. Les clients de votre region peuvent demander une installation via notre site. Notre IA attribue la mission à l’entreprise la mieux adaptée selon la distance et la specialisation.',
    benefits: ['Clients dans votre region', 'Flexible', 'Pas de frais d acquisition', 'Matching IA automatique', 'Tarif horaire a partir de €85'],
    benefitTitles: ['Commandes locales', 'Flexible', 'Pas d acquisition', 'Matching IA', 'Bon tarif'],
    form: { title: 'Inscription', name: 'Entreprise', email: 'E-mail', phone: 'Telephone', website: 'Site web', address: 'Adresse', city: 'Ville', postalCode: 'Code postal', country: 'Pays', radius: 'Distance max. (km)', capabilities: 'Quels services proposez-vous ?', notes: 'Remarques supplementaires', submit: 'S inscrire', submitting: 'Envoi...' },
    success: 'Votre candidature a bien été reçue ! Nous vous contacterons dans les 2 jours ouvrés.',
    error: 'Inscription echouee. Veuillez reessayer ou ecrire a info@printen.eu.',
  },
};

const countries = [
  { code: 'NL', name: { nl: 'Nederland', de: 'Niederlande', en: 'Netherlands', fr: 'Pays-Bas' } },
  { code: 'BE', name: { nl: 'België', de: 'Belgien', en: 'Belgium', fr: 'Belgique' } },
  { code: 'DE', name: { nl: 'Duitsland', de: 'Deutschland', en: 'Germany', fr: 'Allemagne' } },
  { code: 'FR', name: { nl: 'Frankrijk', de: 'Frankreich', en: 'France', fr: 'France' } },
];

const defaultCapabilities = [
  'Lichtbak plaatsen', 'Lichtbak vervangen/onderhoud', 'Raamsticker/folie aanbrengen',
  'Gevelreclame montage', 'Banner/spandoek ophangen', 'Vlaggenmast plaatsen',
  'Bouwhekdoek bevestigen', 'Wanddecoratie/behang aanbrengen', 'Interieur signing',
  'Autobelettering aanbrengen', 'Steigerwerk > 3m', 'Hoogwerker bediening', 'Elektra aansluiten',
];

export default function SamenwerkenPage() {
  const locale = useLocale();
  const safeLocale: Locale = locale in copy ? (locale as Locale) : 'en';
  const t = copy[safeLocale];

  const [form, setForm] = useState({ name: '', email: '', phone: '', website: '', address: '', city: '', postalCode: '', country: 'NL', radius: '50', notes: '' });
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const update = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));
  const toggleCap = (c: string) => setCapabilities(p => p.includes(c) ? p.filter(x => x !== c) : [...p, c]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setStatus('loading');
    try {
      const res = await fetch('/api/sign-companies/register', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, capabilities, serviceRadius: parseInt(form.radius) || 50 }),
      });
      setStatus(res.ok ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-12">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">{t.title}</h1>
      <p className="text-gray-600 mb-10">{t.subtitle}</p>

      <div className="grid md:grid-cols-2 gap-8 mb-12">
        <div>
          <p className="text-gray-700 mb-4">{t.intro}</p>
          <p className="text-gray-700 mb-6">{t.intro2}</p>
          <div className="space-y-4">
            {t.benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-700 flex-none">
                  {i === 0 ? <MapPin className="h-4 w-4" /> : i === 1 ? <ShieldCheck className="h-4 w-4" /> : i === 2 ? <Euro className="h-4 w-4" /> : i === 3 ? <Users className="h-4 w-4" /> : <Wrench className="h-4 w-4" />}
                </div>
                <div><p className="font-semibold">{t.benefitTitles[i]}</p><p className="text-sm text-gray-500">{b}</p></div>
              </div>
            ))}
          </div>
        </div>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">{t.form.title}</h2>

          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mb-4"><Check className="h-8 w-8 text-green-600" /></div>
              <p className="text-lg font-medium text-gray-900">{t.success}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Input value={form.name} onChange={e => update('name', e.target.value)} placeholder={t.form.name + ' *'} required /></div>
                <div><Input type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder={t.form.email + ' *'} required /></div>
                <div><Input value={form.phone} onChange={e => update('phone', e.target.value)} placeholder={t.form.phone} /></div>
              </div>
              <Input value={form.website} onChange={e => update('website', e.target.value)} placeholder={t.form.website} />
              <Input value={form.address} onChange={e => update('address', e.target.value)} placeholder={t.form.address} />
              <div className="grid grid-cols-2 gap-3">
                <Input value={form.postalCode} onChange={e => update('postalCode', e.target.value)} placeholder={t.form.postalCode} />
                <Input value={form.city} onChange={e => update('city', e.target.value)} placeholder={t.form.city} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select className="rounded-lg border border-gray-300 p-2 text-sm" value={form.country} onChange={e => update('country', e.target.value)}>
                  {countries.map(c => <option key={c.code} value={c.code}>{c.name[safeLocale]}</option>)}
                </select>
                <Input type="number" value={form.radius} onChange={e => update('radius', e.target.value)} placeholder={t.form.radius} />
              </div>

              <div>
                <p className="text-sm font-medium mb-2">{t.form.capabilities}</p>
                <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto">
                  {defaultCapabilities.map(c => (
                    <label key={c} className={`flex items-center gap-2 rounded p-1.5 text-xs cursor-pointer border ${capabilities.includes(c) ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}>
                      <input type="checkbox" checked={capabilities.includes(c)} onChange={() => toggleCap(c)} className="rounded" />
                      {c}
                    </label>
                  ))}
                </div>
              </div>

              <Textarea value={form.notes} onChange={e => update('notes', e.target.value)} placeholder={t.form.notes} rows={2} />

              {status === 'error' && <p className="text-sm text-red-600">{t.error}</p>}
              <Button type="submit" className="w-full" disabled={status === 'loading'}>
                {status === 'loading' ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t.form.submitting}</> : t.form.submit}
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
